import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CourseSelectorFieldComponent } from '../course-selector-field/course-selector-field.component';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { DateSelectorType, FixedDate } from '../../types';
import { CourseState, CourseStore } from '../../store';
import { AsyncPipe, NgSwitch, NgSwitchCase } from '@angular/common';
import { DurationSelectorFieldComponent } from '../duration-selector-field/duration-selector-field.component';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FixedDateSelectorFieldComponent } from '../fixed-date-selector-field/fixed-date-selector-field.component';
import { DatesComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { $localize } from '@angular/localize/init';
import { FlexibleStartDateSelectorFieldComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { ChristmasClosureDisclaimerService } from '../../services';
import { TripDatesService } from '@lx/quote-tool/shared/services';
import { CalendarConfig } from '@lx/shared/ui/calendar-field';
import { switchMap } from 'rxjs/operators';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';

@Component({
  standalone: true,
  selector: 'lx-adult-courses',
  templateUrl: './adult-courses.component.html',
  styleUrls: ['./adult-courses.component.scss'],
  imports: [
    CourseSelectorFieldComponent,
    AsyncPipe,
    DurationSelectorFieldComponent,
    FixedDateSelectorFieldComponent,
    DatesComponent,
    FlexibleStartDateSelectorFieldComponent,
    NgSwitch,
    NgSwitchCase,
  ],
})
export class AdultCoursesComponent implements OnInit {
  @Input() courseDetails: CourseState | undefined;
  @Input() automaticOpenDatePanel$: Observable<boolean> | undefined;
  @Output() selectedCourseChange = new EventEmitter<CourseDetails>();
  @Output() selectedDurationChange = new EventEmitter<number>();
  @Output() selectedStartDateChange = new EventEmitter<string>();

  startDateSelectorType: DateSelectorType = 'flexibleDates';
  schoolStartDates: FixedDate[] = [];
  disclaimer$: Observable<string | undefined>;
  calendarConfig: CalendarConfig | undefined;
  readonly emptySelectionMsg = $localize`Select`;

  private readonly courseStore = inject(CourseStore);
  private readonly christmasClosureDisclaimerService = inject(ChristmasClosureDisclaimerService);
  private readonly tripDatesService = inject(TripDatesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);

  protected selectedStartDate = this.qtLiteStoreSignalsAdapterService.selectedStartDate;
  protected selectedEndDate = this.qtLiteStoreSignalsAdapterService.selectedEndDate;

  ngOnInit(): void {
    this.courseStore.selectedCourseAndDuration$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ selectedCourse, selectedDuration }: CourseState) => {
      const fixedDate = selectedCourse?.courseDates?.some((courseDate) => courseDate?.length > 0);
      this.startDateSelectorType = fixedDate ? 'fixedDates' : 'flexibleDates';
      // Check the type of the selected course to determine which component will be displayed to choose the start date
      if (fixedDate) {
        const filteredDates = selectedCourse?.courseDates.filter((courseDate) => courseDate.length === selectedDuration);
        this.schoolStartDates = [...new Set(filteredDates?.map((courseDate) => ({ date: courseDate.from })))];
      }
    });

    // Fetch calendar config based on selected school location
    this.courseStore.selectedSchoolLocation$
      .pipe(
        switchMap((selectedSchoolLocation) => this.tripDatesService.getCalendarConfig(selectedSchoolLocation?.schoolCode)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((calendarConfig) => {
        this.calendarConfig = calendarConfig;
      });

    if (this.courseDetails?.selectedStartDate) {
      this.setChristmasClosureDisclaimer(new Date(this.courseDetails.selectedStartDate).getFullYear());
    }
  }

  setChristmasClosureDisclaimer(year: number): void {
    this.disclaimer$ = this.christmasClosureDisclaimerService.getDisclaimerForSelectedYear(year);
  }
}
